import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, Avatar, RatingBadge, tierLabel, pct, probColor, playColor } from './ui'
import { getNFLTopPlays } from './api'
import { playText } from './plain'

/**
 * Top Plays — every game's top 4–5 plays, ranked by the model's hit probability, with
 * one-tap copy (per game + all games) for extracting/sharing. Reads /nfl/top-plays.
 */
const pctInt = v => `${Math.round((v || 0) * 100)}%`

// Clean text block for the clipboard — reads well pasted into notes / a group chat / a post.
function gameText(g, week) {
  const lines = [`${g.away} @ ${g.home}${week ? ` — Week ${week}` : ''}`]
  g.plays.forEach((p, i) => {
    const r = tierLabel(p.tier)
    const edge = p.edge_pct != null && p.edge_pct >= 1 ? ` · +${p.edge_pct.toFixed(0)}% edge` : ''
    const note = p.tier_note ? ` (${p.tier_note})` : ''
    lines.push(`${i + 1}. ${p.name} · ${playText(p.market, p.line)} · ${r}${note} · ${pctInt(p.hit_prob)}${edge}`)
  })
  return lines.join('\n')
}

function CopyBtn({ text, label = 'Copy', small }) {
  const [done, setDone] = useState(false)
  const copy = () => {
    const ok = () => { setDone(true); setTimeout(() => setDone(false), 1500) }
    try {
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(ok, ok)
      else { const t = document.createElement('textarea'); t.value = text; document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove(); ok() }
    } catch (_) { ok() }
  }
  return (
    <button onClick={copy} style={{ cursor: 'pointer', fontFamily: T.mono, fontWeight: 700,
      fontSize: small ? 10.5 : 12, padding: small ? '4px 9px' : '7px 12px', borderRadius: 8,
      color: done ? '#04140D' : T.green, background: done ? T.green : T.greenDim,
      border: `1px solid ${T.green}${done ? '' : '55'}`, whiteSpace: 'nowrap' }}>
      {done ? '✓ Copied' : `⧉ ${label}`}
    </button>
  )
}

function PlayRow({ p, i }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0',
      borderTop: i === 0 ? 'none' : `1px solid ${T.border}` }}>
      <span style={{ fontFamily: T.mono, fontSize: 11, color: T.faint, width: 12 }}>{i + 1}</span>
      <RatingBadge tier={p.tier} size={26} />
      <Avatar url={p.headshot} name={p.name} team={p.team} size={26} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
        <div style={{ fontSize: 10.5, color: T.muted }}>{playText(p.market, p.line)}
          {p.edge_pct != null && p.edge_pct >= 1 ? <span style={{ color: T.green }}> · +{p.edge_pct.toFixed(0)}%</span> : null}
          {p.value ? <span style={{ color: T.green }}> · ✓ value</span> : null}
          {p.tier_note ? <span style={{ color: T.amber, fontWeight: 700 }}> · {p.tier_note}</span> : null}
          {p.live ? (() => {
            const lv = p.live, td = p.market === 'Anytime TD'
            const c = lv.status === 'hit' ? T.green : lv.status === 'miss' ? T.red : T.text
            const txt = td ? (lv.cleared ? 'scored ✓' : lv.state === 'post' ? 'no TD' : 'no TD yet')
              : `${lv.actual}/${lv.line}${lv.status === 'hit' ? ' ✓' : ''}`
            return <span style={{ color: c, fontWeight: 700 }}> · {lv.state === 'in' ? '● ' : ''}{txt}</span>
          })() : null}
        </div>
      </div>
      <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 14, color: playColor(p.hit_prob || 0, p) }}>{pctInt(p.hit_prob)}</span>
    </div>
  )
}

// Filter definitions. Positions + bet types map to the fields each play carries; windows
// are discovered from the week's schedule (only the ones that exist show up). Bet types are
// the markets the model actually projects — rush/pass ATTEMPTS aren't modeled, so they're
// not offered (the model produces rush/pass YARDS + TDs, not attempts).
const POS_FILTERS = ['All', 'QB', 'RB', 'WR', 'TE']
const BET_FILTERS = [
  ['All', null], ['TD', 'Anytime TD'], ['Receptions', 'Receptions'], ['Rec Yds', 'Rec Yds'],
  ['Rush Yds', 'Rush Yds'], ['Pass Yds', 'Pass Yds'], ['Pass TDs', 'Pass TDs'],
]
// Preferred display order for the window chips (only those present this week render).
const WINDOW_ORDER = ['Thursday', 'Sun AM', 'Sun early', 'Sun late', 'Sun night', 'Monday', 'Friday', 'Saturday', 'Sunday']

function Chip({ on, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      cursor: 'pointer', fontSize: 11.5, fontWeight: 800, padding: '6px 12px', borderRadius: 8,
      whiteSpace: 'nowrap', border: `1px solid ${on ? T.green : T.border}`,
      background: on ? T.greenDim : T.card, color: on ? T.green : T.muted }}>
      {children}
    </button>
  )
}
function ChipRow({ label, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: T.faint, marginBottom: 5, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2, WebkitOverflowScrolling: 'touch' }}>{children}</div>
    </div>
  )
}

export default function TopPlays({ season, week }) {
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sample, setSample] = useState(false)
  const [pos, setPos] = useState('All')
  const [bet, setBet] = useState('All')       // BET_FILTERS label
  const [win, setWin] = useState('All')        // window label
  useEffect(() => {
    let a = true; setLoading(true)
    // Pull the fuller board (up to 8/game) so filtering by position/bet type still has depth.
    getNFLTopPlays(season, week, 8).then(r => { if (!a) return; setD(r); setSample(!!r._sample); setLoading(false) })
    return () => { a = false }
  }, [season, week])

  // While a game is live, refresh every 30s so the inline live progress ticks up; stop when
  // the slate goes quiet. Rows only (no spinner).
  const hasLive = !!d?.has_live
  useEffect(() => {
    if (!hasLive) return
    let a = true
    const id = setInterval(() => {
      getNFLTopPlays(season, week, 8).then(r => { if (a) setD(r) })
    }, 30000)
    return () => { a = false; clearInterval(id) }
  }, [hasLive, season, week])

  if (loading) return <Spinner />
  const games = d?.games || []
  const betMarket = (BET_FILTERS.find(b => b[0] === bet) || [])[1] || null

  // Windows present this week, in preferred order, for the chip row.
  const present = [...new Set(games.map(g => g.window).filter(Boolean))]
  const windows = WINDOW_ORDER.filter(w => present.includes(w))

  // Apply the filters: window gates the GAME, position + bet type gate each PLAY. A game with
  // no matching plays after filtering drops out. Nothing is re-ranked — the backend already
  // ordered each game's plays by hit %.
  const filtered = games
    .filter(g => win === 'All' || g.window === win)
    .map(g => ({ ...g, plays: (g.plays || []).filter(p =>
      (pos === 'All' || p.pos === pos) && (!betMarket || p.market === betMarket)) }))
    .filter(g => g.plays.length)

  const anyFilter = pos !== 'All' || bet !== 'All' || win !== 'All'
  const reset = () => { setPos('All'); setBet('All'); setWin('All') }
  const nPlays = filtered.reduce((s, g) => s + g.plays.length, 0)
  const allText = filtered.map(g => gameText(g, week)).join('\n\n')

  return (
    <div>
      {sample && <div style={{ fontSize: 10, color: T.amber, marginBottom: 8, letterSpacing: '0.05em' }}>● Live data unavailable right now — check back shortly</div>}

      {/* Filter bar */}
      <ChipRow label="Position">
        {POS_FILTERS.map(p => <Chip key={p} on={pos === p} onClick={() => setPos(p)}>{p}</Chip>)}
      </ChipRow>
      <ChipRow label="Bet type">
        {BET_FILTERS.map(([lab]) => <Chip key={lab} on={bet === lab} onClick={() => setBet(lab)}>{lab}</Chip>)}
      </ChipRow>
      {windows.length > 1 && (
        <ChipRow label="Game window">
          <Chip on={win === 'All'} onClick={() => setWin('All')}>All</Chip>
          {windows.map(w => <Chip key={w} on={win === w} onClick={() => setWin(w)}>{w}</Chip>)}
        </ChipRow>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, margin: '10px 0' }}>
        <div style={{ fontSize: 11, color: T.muted }}>
          {anyFilter ? `${nPlays} play${nPlays === 1 ? '' : 's'} · ${filtered.length} game${filtered.length === 1 ? '' : 's'}` : 'Top plays per game · ranked by hit %'}
          {anyFilter && <button onClick={reset} style={{ marginLeft: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700, color: T.green, background: 'none', border: 'none', padding: 0 }}>Clear</button>}
        </div>
        {filtered.length > 0 && <CopyBtn text={allText} label="Copy shown" />}
      </div>

      {!games.length ? <Empty message="No plays for this week yet" />
        : !filtered.length ? <Empty message="No plays match these filters — try clearing one." />
        : filtered.map(g => (
          <div key={g.game_id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800 }}>{g.away} <span style={{ color: T.muted, fontWeight: 600 }}>@</span> {g.home}</div>
                <div style={{ fontSize: 10.5, color: T.muted, fontFamily: T.mono }}>
                  {g.window || (g.gameday ? g.gameday.slice(5) : '')}{g.total != null ? ` · O/U ${g.total}` : ''}
                </div>
              </div>
              <CopyBtn text={gameText(g, week)} small />
            </div>
            {g.plays.map((p, i) => <PlayRow key={i} p={p} i={i} />)}
          </div>
        ))}
      <div style={{ fontSize: 10, color: T.faint, marginTop: 6, lineHeight: 1.5 }}>
        Ranked by the model's hit probability at the shown line (book line when posted). Informational only · 21+.
      </div>
    </div>
  )
}
